/**
 * src/pages/AI.tsx
 *
 * Pipeline tab (was "AI"). Since 2026-09-25 the top of the page watches the
 * pipeline in the team design doc: Jev reading titles, the backup AI, the
 * catalogue decisions, the photo check, what goes out, and the catalogue
 * itself, with add / remove. See pages/pipeline/PipelinePanels.tsx.
 *
 * Below that: the call receipts (Jev and backup both write them), spend, the
 * gate guard, and the backup AI's prompt.
 *
 * Original header, for the receipt panels:
 * AI tab — classifier telemetry and the prompt editor.
 * Backs docs/pipeline/PIPELINE_V2_DESIGN.md §8A ("nothing goes to a model
 * without a receipt").
 *
 * Sections:
 *   A. Status strip     — enabled / shadow / model / breaker / today's spend
 *   B. Spend + runway   — burn rate, operator-entered credit, cost per EMIT
 *   C. Efficiency       — cache hit rates, escalation rate, latency
 *   D. Reliability      — failure mix by outcome
 *   E. Daily table      — the per-day rollup behind everything above
 *   F. Prompt editor    — view / edit / revert the system prompt
 *
 * On balance: the provider does not expose an account balance on our API key
 * (every billing path 404s), so "credit" is a number an operator types in and
 * runway is credit ÷ measured burn. The UI says so rather than implying we
 * read the account.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, AlertTriangle, Check, RotateCcw, Zap, Layers, Server, ShieldCheck } from 'lucide-react';
import {
  fetchAiUsage, fetchAiStatus, fetchAiPrompt, saveAiPrompt, saveAiCredit,
  fetchAiGateGuard,
  type AiUsage, type AiStatus, type AiPrompt, type AiGateGuard,
} from '@/api/ai';
import {
  StatusStrip, WindowToggle, SummaryPanels, MissesPanel, CataloguePanel, BrandSuggestionsPanel,
} from './pipeline/PipelinePanels';
import type { AddProductInput } from '@/api/pipeline';
import ChartCard from '@/components/ChartCard';
import { cn } from '@/lib/cn';

const WINDOWS = [7, 30, 90] as const;

function gbp(n: number | null | undefined, dp = 4): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `£${n.toFixed(dp)}`;
}
function pct(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}
function num(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  return n.toLocaleString();
}

function Stat({ label, value, hint, tone, small }: {
  label: string; value: string; hint?: string;
  tone?: 'good' | 'warn' | 'bad';
  /** For long non-numeric values (model ids) that would otherwise truncate. */
  small?: boolean;
}) {
  return (
    <div className="rounded border border-slate-800 bg-slate-950/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={cn(
        'mt-0.5 font-semibold tabular-nums',
        small ? 'break-words text-sm' : 'truncate text-lg',
        tone === 'good' && 'text-emerald-400',
        tone === 'warn' && 'text-amber-400',
        tone === 'bad'  && 'text-red-400',
        !tone && 'text-slate-100',
      )}>{value}</div>
      {hint && <div className="mt-0.5 break-words text-[11px] leading-tight text-slate-500">{hint}</div>}
    </div>
  );
}

export default function AI() {
  const [days, setDays] = useState<number>(30);
  const [hours, setHours] = useState<number>(24);
  const [addSeed, setAddSeed] = useState<Partial<AddProductInput> | null>(null);
  const qc = useQueryClient();

  const status = useQuery<AiStatus>({
    queryKey: ['ai', 'status'],
    queryFn: fetchAiStatus,
    refetchInterval: 30_000,
  });
  const usage = useQuery<AiUsage>({
    queryKey: ['ai', 'usage', days],
    queryFn: () => fetchAiUsage(days),
    refetchInterval: 60_000,
  });

  const u = usage.data;
  const s = status.data;

  return (
    <div className="space-y-4 p-4">
      <header>
        <h1 className="text-lg font-semibold text-slate-100">Pipeline</h1>
        <p className="text-xs text-slate-500">
          Jev reads every title and picks the product from our catalogue; the backup AI only
          runs when Jev fails; a small image model checks the photo. Everything here reads
          stored decisions, nothing is estimated.
        </p>
      </header>

      <StatusStrip />
      <WindowToggle hours={hours} onChange={setHours} />
      <SummaryPanels hours={hours} />
      <BrandSuggestionsPanel hours={hours} />
      <MissesPanel hours={hours} onAdd={seed => {
        setAddSeed(seed);
        setTimeout(() => document.getElementById('catalogue-panel')?.scrollIntoView({ behavior: 'smooth' }), 50);
      }} />
      <div id="catalogue-panel">
        <CataloguePanel addSeed={addSeed} setAddSeed={setAddSeed} />
      </div>

      <h2 className="pt-4 text-sm font-semibold text-slate-300">Call receipts (Jev and backup AI)</h2>

      {/* ── A. Status strip ─────────────────────────────────────────── */}
      <ChartCard
        title="Classifier status"
        subtitle={s ? `verdict version ${s.verdictVersion}` : undefined}
        isLoading={status.isLoading}
        isError={status.isError}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Stat
            label="Classifier"
            value={s?.enabled ? 'Enabled' : 'Disabled'}
            tone={s?.enabled ? 'good' : undefined}
            hint={s?.enabled ? undefined : 'AI_CLASSIFIER_ENABLED=0'}
          />
          <Stat
            label="Mode"
            value={s?.shadowMode ? 'Shadow' : 'Live'}
            tone={s?.shadowMode ? undefined : 'warn'}
            hint={s?.shadowMode ? 'cannot change a decision' : 'verdicts affect decisions'}
          />
          <Stat label="Model" value={s?.model ?? '—'} small
                hint={s?.escalationModel ? `escalates to ${s.escalationModel}` : undefined} />
          <Stat label="Sample rate" value={s?.sampleRate != null ? pct(s.sampleRate) : '—'} />
          <Stat
            label="Gates in effect"
            value={
              s?.gates
                ? (Object.entries(s.gates).filter(([, on]) => on).map(([k]) => k).join(', ') || 'none')
                : '—'
            }
            small
            tone={s?.canDecide === false ? 'warn' : undefined}
            hint={
              s?.canDecide === false
                ? 'shadow mode — no gate can act whatever these say'
                : s?.startedAt
                ? `running since ${new Date(s.startedAt).toLocaleString()} — a switch changed after this needs a restart`
                : undefined
            }
          />
          <Stat
            label="Breaker"
            value={s?.runtime ? (s.runtime.breaker.open ? 'OPEN' : 'Closed') : '—'}
            tone={s?.runtime?.breaker.open ? 'bad' : undefined}
            hint={s?.runtime ? `${s.runtime.breaker.failures} consecutive failures` : undefined}
          />
          <Stat
            label="Today's spend"
            value={s?.runtime ? gbp(s.runtime.spend.spentGbp, 4) : '—'}
            tone={
              s?.runtime && s.runtime.spend.ceilingGbp > 0 &&
              s.runtime.spend.spentGbp >= s.runtime.spend.ceilingGbp ? 'bad' : undefined
            }
            hint={s?.runtime ? `ceiling ${gbp(s.runtime.spend.ceilingGbp, 2)}/day` : undefined}
          />
        </div>
        {s && !s.enabled && (
          <p className="mt-3 rounded border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs text-slate-400">
            The classifier is off, so these panels will stay empty. Enable it by
            setting <code className="text-slate-300">AI_CLASSIFIER_ENABLED=1</code> in
            the host env file and restarting <code className="text-slate-300">otr-core</code>.
          </p>
        )}
      </ChartCard>

      {/* window toggle */}
      <div className="flex items-center gap-1">
        {WINDOWS.map(w => (
          <button
            key={w}
            onClick={() => setDays(w)}
            className={cn(
              'rounded px-2 py-1 text-xs',
              days === w
                ? 'bg-slate-700 text-slate-100'
                : 'bg-slate-900 text-slate-400 hover:bg-slate-800',
            )}
          >{w}d</button>
        ))}
      </div>

      {/* ── B. Spend and runway ─────────────────────────────────────── */}
      <SpendPanel usage={u} loading={usage.isLoading} error={usage.isError}
                  onSaved={() => qc.invalidateQueries({ queryKey: ['ai', 'usage'] })} />

      {/* ── C. Efficiency ───────────────────────────────────────────── */}
      <ChartCard
        title="Efficiency"
        subtitle="Is the caching actually paying for itself?"
        isLoading={usage.isLoading}
        isError={usage.isError}
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Stat label="Prompt cache hit" value={pct(u?.cacheHitRate)}
                hint="cached input tokens / all input tokens" />
          <Stat label="Image cache hit" value={pct(u?.imageCacheHitRate)}
                hint="repeat images skipped by md5" />
          <Stat label="Escalation rate" value={pct(u?.escalationRate)}
                hint="low-confidence re-asks on the bigger model" />
          <Stat label="p50 latency" value={u ? `${num(u.p50LatencyMs)} ms` : '—'} />
          <Stat label="p95 latency" value={u ? `${num(u.p95LatencyMs)} ms` : '—'}
                hint="worst observed day" />
        </div>
      </ChartCard>

      {/* ── D. Reliability ──────────────────────────────────────────── */}
      <ChartCard
        title="Reliability"
        subtitle="Failures are recorded too — a degrading model and a healthy one look identical without them."
        isLoading={usage.isLoading}
        isError={usage.isError}
        isEmpty={!!u && u.totals.calls === 0}
        emptyLabel="No calls recorded in this window."
      >
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Calls" value={num(u?.totals.calls)} />
          <Stat label="Succeeded" value={num(u?.totals.ok)} tone="good" />
          <Stat label="Failed" value={num(u?.totals.failures)}
                tone={u && u.failureRate > 0.1 ? 'bad' : undefined} />
          <Stat label="Failure rate" value={pct(u?.failureRate)}
                tone={u && u.failureRate > 0.1 ? 'bad' : undefined} />
        </div>
        {u && Object.keys(u.outcomes).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {Object.entries(u.outcomes).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <span key={k} className={cn(
                'rounded border px-2 py-0.5 text-xs',
                k === 'ok'
                  ? 'border-emerald-900 bg-emerald-950/40 text-emerald-300'
                  : 'border-amber-900 bg-amber-950/40 text-amber-300',
              )}>{k}: {num(v)}</span>
            ))}
          </div>
        )}
      </ChartCard>

      {/* ── E. Models in Use & Routing ──────────────────────────────── */}
      <ModelsBreakdownPanel usage={u} loading={usage.isLoading} error={usage.isError} />

      {/* ── F. Daily table ──────────────────────────────────────────── */}
      <ChartCard
        title="Daily"
        isLoading={usage.isLoading}
        isError={usage.isError}
        isEmpty={!u?.daily.length}
        emptyLabel="No days with recorded calls yet."
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-500">
              <tr className="border-b border-slate-800">
                <th className="py-1 text-left font-medium">Day</th>
                <th className="py-1 text-right font-medium">Calls</th>
                <th className="py-1 text-right font-medium">Failed</th>
                <th className="py-1 text-right font-medium">Cost</th>
                <th className="py-1 text-right font-medium">Cache hit</th>
                <th className="py-1 text-right font-medium">Escalations</th>
                <th className="py-1 text-right font-medium">p95</th>
                <th className="py-1 text-left font-medium pl-3">Models Active</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              {u?.daily.map(d => (
                <tr key={d.day} className="border-b border-slate-900">
                  <td className="py-1">{d.day}</td>
                  <td className="py-1 text-right tabular-nums">{num(d.calls)}</td>
                  <td className={cn('py-1 text-right tabular-nums', d.failures > 0 && 'text-amber-400')}>{num(d.failures)}</td>
                  <td className="py-1 text-right tabular-nums">{gbp(d.costGbp, 5)}</td>
                  <td className="py-1 text-right tabular-nums">{pct(d.cacheHitRate)}</td>
                  <td className="py-1 text-right tabular-nums">{num(d.escalations)}</td>
                  <td className="py-1 text-right tabular-nums">{num(d.p95LatencyMs)} ms</td>
                  <td className="py-1 pl-3">
                    <div className="flex flex-wrap gap-1">
                      {d.models && Object.keys(d.models).length > 0 ? (
                        Object.entries(d.models).map(([mName, mStats]) => {
                          const info = getModelTierInfo(mName);
                          return (
                            <span
                              key={mName}
                              title={`${mName} (${info.tier}): ${mStats.calls} calls (${mStats.ok} ok, ${mStats.failures} fail, avg ${mStats.avgLatencyMs}ms)`}
                              className={cn(
                                'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-mono',
                                info.badge
                              )}
                            >
                              <span className="truncate max-w-[120px] font-medium">{mName}</span>:
                              <span className="ml-1 font-semibold">{mStats.calls}</span>
                              {mStats.failures > 0 && <span className="ml-0.5 text-amber-400">({mStats.failures}!)</span>}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ChartCard>

      {/* ── F. Gate guard ───────────────────────────────────────────── */}
      <GateGuardPanel />

      {/* ── I. Prompt editor ────────────────────────────────────────── */}
      <PromptEditor />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

function getModelTierInfo(model: string): { tier: string; badge: string; isZeroCost: boolean } {
  const m = model.toLowerCase();
  // Tier 4: Paid Cloud API (Alibaba DashScope Qwen / Paid Models)
  if (m.includes('qwen3') || m.includes('dashscope') || m.includes('qwen-plus') || m.includes('qwen-max') || m.includes('qwen-turbo') || m.includes('qwen-vl')) {
    return { tier: 'Tier 4: Paid Cloud (Alibaba Qwen)', badge: 'bg-amber-950/60 text-amber-300 border-amber-800', isZeroCost: false };
  }
  // Tier 0: Local ONNX Runtime mbertX
  if (m.includes('mbert') || m.includes('onnx')) {
    return { tier: 'Tier 0: Local ONNX (mbertX)', badge: 'bg-emerald-950/60 text-emerald-300 border-emerald-800', isZeroCost: true };
  }
  // Tier 3: Local Ollama (on-box small models only)
  if (m.startsWith('qwen2.5') || m.startsWith('qwen2') || m.includes('ollama') || m.startsWith('moondream') || m.startsWith('local')) {
    return { tier: 'Tier 3: Local Ollama (On-Box)', badge: 'bg-purple-950/60 text-purple-300 border-purple-800', isZeroCost: true };
  }
  // Tier 2: OpenRouter Free Models
  if (m.endsWith(':free') || m.includes('openrouter') || m.includes('nemotron') || m.includes('lfm')) {
    return { tier: 'Tier 2: OpenRouter Free', badge: 'bg-cyan-950/60 text-cyan-300 border-cyan-800', isZeroCost: true };
  }
  // Tier 1: Cloud Direct Free (Gemini)
  if (m.includes('gemini')) {
    return { tier: 'Tier 1: Cloud Direct (Free)', badge: 'bg-blue-950/60 text-blue-300 border-blue-800', isZeroCost: true };
  }
  return { tier: 'Tier 4: Paid Cloud (Escalation)', badge: 'bg-amber-950/60 text-amber-300 border-amber-800', isZeroCost: false };
}

function ModelsBreakdownPanel({ usage, loading, error }: {
  usage?: AiUsage; loading: boolean; error: boolean;
}) {
  const models = usage?.models ?? {};
  const modelEntries = Object.entries(models).sort((a, b) => b[1].calls - a[1].calls);

  return (
    <ChartCard
      title="Multi-Tier Models in Use & Routing Performance"
      subtitle={`Inference telemetry breakdown across all active models in the last ${usage?.windowDays ?? 30} days.`}
      isLoading={loading}
      isError={error}
      isEmpty={modelEntries.length === 0}
      emptyLabel="No models recorded in this window."
    >
      <div className="mb-3 flex flex-wrap gap-2 text-[11px] text-slate-400">
        <span className="flex items-center gap-1 rounded border border-amber-800/60 bg-amber-950/40 px-2 py-1 text-amber-300">
          <AlertTriangle className="h-3 w-3 text-amber-400" />
          <strong>Paid Cloud:</strong> Alibaba DashScope (£54.77 spend)
        </span>
        <span className="flex items-center gap-1 rounded border border-emerald-800/60 bg-emerald-950/40 px-2 py-1 text-emerald-300">
          <Zap className="h-3 w-3 text-emerald-400" />
          <strong>Tier 0:</strong> Local ONNX Runtime (mbertX) (£0.00)
        </span>
        <span className="flex items-center gap-1 rounded border border-blue-800/60 bg-blue-950/40 px-2 py-1 text-blue-300">
          <Layers className="h-3 w-3 text-blue-400" />
          <strong>Tier 1:</strong> Gemini Flash Direct (Free Quota 500/d)
        </span>
        <span className="flex items-center gap-1 rounded border border-cyan-800/60 bg-cyan-950/40 px-2 py-1 text-cyan-300">
          <ShieldCheck className="h-3 w-3 text-cyan-400" />
          <strong>Tier 2:</strong> OpenRouter Free Fallback (£0.00)
        </span>
        <span className="flex items-center gap-1 rounded border border-purple-800/60 bg-purple-950/40 px-2 py-1 text-purple-300">
          <Server className="h-3 w-3 text-purple-400" />
          <strong>Tier 3:</strong> Hetzner Local Ollama Sidecar (£0.00)
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-slate-500">
            <tr className="border-b border-slate-800">
              <th className="py-1 text-left font-medium">Model</th>
              <th className="py-1 text-left font-medium">Routing Tier</th>
              <th className="py-1 text-right font-medium">Calls</th>
              <th className="py-1 text-right font-medium">Success Rate</th>
              <th className="py-1 text-right font-medium">Succeeded (OK)</th>
              <th className="py-1 text-right font-medium">Failed</th>
              <th className="py-1 text-right font-medium">Avg Latency</th>
              <th className="py-1 text-right font-medium">Total Cost</th>
              <th className="py-1 text-left font-medium pl-3">Outcomes</th>
            </tr>
          </thead>
          <tbody className="text-slate-300">
            {modelEntries.map(([modelName, m]) => {
              const info = getModelTierInfo(modelName);
              const successRate = m.calls > 0 ? (m.ok / m.calls) : 0;
              return (
                <tr key={modelName} className="border-b border-slate-900 hover:bg-slate-900/30">
                  <td className="py-2 font-mono text-[11px] text-slate-100">{modelName}</td>
                  <td className="py-2">
                    <span className={cn('rounded border px-1.5 py-0.5 text-[10px] font-medium', info.badge)}>
                      {info.tier}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums">{num(m.calls)}</td>
                  <td className="py-2 text-right tabular-nums">
                    <span className={cn(
                      'font-medium',
                      successRate >= 0.95 ? 'text-emerald-400' : successRate >= 0.8 ? 'text-amber-400' : 'text-red-400'
                    )}>
                      {pct(successRate)}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums text-emerald-400">{num(m.ok)}</td>
                  <td className={cn('py-2 text-right tabular-nums', m.failures > 0 ? 'text-amber-400 font-medium' : 'text-slate-500')}>
                    {num(m.failures)}
                  </td>
                  <td className="py-2 text-right tabular-nums">{num(m.avgLatencyMs)} ms</td>
                  <td className="py-2 text-right tabular-nums">
                    {m.costGbp > 0 ? (
                      <span className="font-mono font-medium text-amber-300">{gbp(m.costGbp, 4)}</span>
                    ) : (
                      <span className="text-emerald-400 font-mono font-medium">£0.000 (Free)</span>
                    )}
                  </td>
                  <td className="py-2 pl-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(m.outcomes).map(([k, count]) => (
                        <span key={k} className={cn(
                          'rounded px-1 py-0.2 text-[10px]',
                          k === 'ok' ? 'bg-emerald-950/60 text-emerald-300' : 'bg-amber-950/60 text-amber-300'
                        )}>
                          {k}: {num(count)}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </ChartCard>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

function SpendPanel({ usage, loading, error, onSaved }: {
  usage?: AiUsage; loading: boolean; error: boolean; onSaved: () => void;
}) {
  const [credit, setCredit] = useState<string>('');
  const [saved, setSaved] = useState(false);

  const save = useMutation({
    mutationFn: (v: number) => saveAiCredit(v),
    onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2500); onSaved(); },
  });

  const runwayLabel = (() => {
    if (!usage) return '—';
    const r = usage.runway;
    if (r.reason === 'ok' && r.days !== null) return `${num(r.days)} days`;
    if (r.reason === 'no_credit_set') return 'Set credit →';
    if (r.reason === 'no_burn') return 'No spend yet';
    return 'No data';
  })();

  return (
    <ChartCard
      title="Spend and runway"
      subtitle={`Burn measured from receipts over ${usage?.windowDays ?? '—'} days.`}
      isLoading={loading}
      isError={error}
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Window spend" value={gbp(usage?.totals.costGbp, 4)} />
        <Stat label="Burn / day" value={gbp(usage?.burn.gbpPerDay, 4)}
              hint={usage ? `over ${usage.burn.daysObserved} active day(s)` : undefined} />
        <Stat label="Today" value={gbp(usage?.burn.gbpLast24h, 4)}
              tone={usage && usage.burn.gbpLast24h > usage.burn.gbpPerDay * 2 ? 'warn' : undefined}
              hint={usage && usage.burn.gbpLast24h > usage.burn.gbpPerDay * 2 ? 'above average' : undefined} />
        <Stat label="Per 1k classified" value={gbp(usage?.gbpPer1kClassified, 3)} />
        <Stat label="Cost per EMIT" value={gbp(usage?.gbpPerEmit, 5)}
              hint={usage ? `${num(usage.emitsInWindow)} emits in window` : undefined} />
        <Stat label="Runway" value={runwayLabel}
              tone={usage?.runway.days !== null && usage?.runway.days !== undefined && usage.runway.days < 14 ? 'warn' : undefined}
              hint={usage?.runway.creditGbp !== null && usage?.runway.creditGbp !== undefined
                ? `credit ${gbp(usage.runway.creditGbp, 2)}` : undefined} />
      </div>

      <div className="mt-3 rounded border border-slate-800 bg-slate-950/40 p-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-xs text-slate-400">
            <span className="mb-1 block">Credit remaining (£)</span>
            <input
              className="input w-40"
              type="number" min="0" step="0.01"
              placeholder={usage?.runway.creditGbp?.toString() ?? 'e.g. 50'}
              value={credit}
              onChange={e => setCredit(e.target.value)}
            />
          </label>
          <button
            className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 disabled:opacity-50"
            disabled={save.isPending || credit.trim() === '' || Number(credit) < 0}
            onClick={() => save.mutate(Number(credit))}
          >
            {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
          </button>
          {saved && <span className="flex items-center gap-1 text-xs text-emerald-400"><Check className="h-3 w-3" /> Saved</span>}
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
          Entered by hand on purpose. Our API key is a model key — the provider
          exposes no account balance through it, so runway is this figure divided
          by the burn rate measured from our own call receipts. It is not a
          reading of the account.
        </p>
      </div>
    </ChartCard>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

function PromptEditor() {
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery<AiPrompt>({
    queryKey: ['ai', 'prompt'],
    queryFn: fetchAiPrompt,
  });

  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [justSaved, setJustSaved] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (input: { systemPrompt: string | null; note?: string }) => saveAiPrompt(input),
    onSuccess: (r) => {
      setJustSaved(r.effectivePromptVersion);
      setDraft(null);
      setNote('');
      qc.invalidateQueries({ queryKey: ['ai', 'prompt'] });
      qc.invalidateQueries({ queryKey: ['ai', 'status'] });
      setTimeout(() => setJustSaved(null), 6000);
    },
  });

  const current = draft ?? data?.effectiveSystemPrompt ?? '';
  const dirty = draft !== null && draft !== data?.effectiveSystemPrompt;

  return (
    <ChartCard
      title="System prompt"
      subtitle={data
        ? `${data.effectivePromptVersion}${data.isOverridden ? ` — overridden (revision ${data.revision})` : ' — shipping default'}`
        : undefined}
      isLoading={isLoading}
      isError={isError}
      actions={data?.updatedAt ? (
        <span className="text-[11px] text-slate-500">
          last edited {new Date(data.updatedAt).toLocaleString()}
          {data.updatedBy ? ` by ${data.updatedBy}` : ''}
        </span>
      ) : undefined}
    >
      <div className="rounded border border-amber-900/60 bg-amber-950/20 px-3 py-2 text-[11px] leading-relaxed text-amber-200/90">
        <AlertTriangle className="mr-1 inline h-3 w-3" />
        Saving bumps the prompt revision, which is part of <code>verdict_version</code>.
        Every stored verdict then re-classifies lazily the next time its listing is
        seen — that is deliberate (a prompt edit changes verdicts as much as a
        model swap) but it does mean a spend bump follows an edit.
      </div>

      <textarea
        className="input mt-3 h-96 w-full font-mono text-[11px] leading-relaxed"
        spellCheck={false}
        value={current}
        onChange={e => setDraft(e.target.value)}
      />

      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex-1 text-xs text-slate-400">
          <span className="mb-1 block">Why are you changing it?</span>
          <input
            className="input w-full"
            placeholder="e.g. product_type still leaking category values on monitors"
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </label>
        <button
          className="rounded bg-slate-700 px-3 py-1 text-xs text-slate-100 hover:bg-slate-600 disabled:opacity-50"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate({ systemPrompt: draft, note: note || undefined })}
        >
          {save.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save prompt'}
        </button>
        {dirty && (
          <button
            className="rounded bg-slate-900 px-3 py-1 text-xs text-slate-400 hover:bg-slate-800"
            onClick={() => setDraft(null)}
          >Discard</button>
        )}
        {data?.isOverridden && (
          <button
            className="flex items-center gap-1 rounded bg-slate-900 px-3 py-1 text-xs text-slate-400 hover:bg-slate-800 disabled:opacity-50"
            disabled={save.isPending}
            onClick={() => save.mutate({ systemPrompt: null, note: note || 'revert to shipping default' })}
            title="Discard the override and go back to the prompt that ships in code"
          >
            <RotateCcw className="h-3 w-3" /> Revert to default
          </button>
        )}
        {justSaved && (
          <span className="flex items-center gap-1 text-xs text-emerald-400">
            <Check className="h-3 w-3" /> Saved as {justSaved}
          </span>
        )}
      </div>

      <details className="mt-4">
        <summary className="cursor-pointer text-xs text-slate-400">
          User prompt (per listing — structural, not editable)
        </summary>
        <p className="mt-1 text-[11px] text-slate-500">
          Built per listing from title, price and source. It is not editable
          because a free-text template here could drop the title and silently
          classify nothing.
        </p>
        <pre className="mt-2 overflow-x-auto rounded border border-slate-800 bg-slate-950/60 p-2 font-mono text-[11px] text-slate-300">
{data?.userPromptExample}
        </pre>
      </details>

      {data && (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs text-slate-400">
            Categories injected at call time ({data.categories.length})
          </summary>
          <p className="mt-1 text-[11px] text-slate-500">
            Always appended from config, even to an override — scope must not be
            able to drift out of sync with PHASE_0_CATEGORIES via a prompt edit.
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            {data.categories.map(c => (
              <span key={c} className="rounded border border-slate-800 bg-slate-950/60 px-2 py-0.5 text-[11px] text-slate-300">{c}</span>
            ))}
          </div>
        </details>
      )}
    </ChartCard>
  );
}

/* ────────────────────────────────────────────────────────────────────── */

/**
 * Whether any gate has switched itself off.
 *
 * A gate that has tripped is passing everything through ungated, which looks
 * exactly like a healthy quiet gate from every other panel. It needs to be
 * visible somewhere that is not a container log.
 */
function GateGuardPanel() {
  const { data, isLoading, isError } = useQuery<AiGateGuard>({
    queryKey: ['ai', 'gate-guard'],
    queryFn: () => fetchAiGateGuard(),
    refetchInterval: 30_000,
  });

  const tripped = data?.gates.filter(g => g.tripped) ?? [];

  return (
    <ChartCard
      title="Gate guard"
      subtitle={
        data
          ? `disables a gate above ${pct(data.config.maxDropRate)} drops over ${num(data.config.windowSize)} listings`
          : undefined
      }
      isLoading={isLoading}
      isError={isError}
    >
      {tripped.length > 0 && (
        <div className="mb-3 flex items-start gap-2 rounded border border-red-800 bg-red-950/40 p-2 text-xs text-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>{tripped.map(g => g.gate).join(', ')}</strong> disabled {tripped.length > 1 ? 'themselves' : 'itself'}.
            Those listings are passing through ungated. The gate stays off until this is
            looked at and the process restarted.
          </div>
        </div>
      )}

      {data && data.gates.length === 0 && (
        <p className="text-xs text-slate-500">
          No gate has judged a listing since the last restart. The window is per-process.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {data?.gates.map(g => (
          <Stat
            key={g.gate}
            label={g.gate}
            value={pct(g.dropRate)}
            tone={g.tripped ? 'bad' : g.dropRate > data.config.maxDropRate * 0.75 ? 'warn' : 'good'}
            hint={
              g.tripped
                ? `disabled ${g.trippedAt ? new Date(g.trippedAt).toLocaleTimeString() : ''}`
                : `${num(g.samples)} judged${g.samples < data.config.minSamples ? ' (below trip minimum)' : ''}`
            }
          />
        ))}
      </div>
    </ChartCard>
  );
}
